import moment from 'moment';
import { TimesheetState } from '../timesheet-collection.state';

/** The start/end span of a timesheet entry — all the ordering and end-of-work rules look at. */
type EntrySpan = {
    start: moment.Moment;
    end: moment.Moment;
};

/**
 * Port of `TimesheetModel.sortTimesheetEntries` reduced to the entries' spans: order by start
 * datetime, tie-broken by end datetime; entries with an inverted (negative) duration are treated
 * as equal.
 *
 * @param spans the merged notification + manual entry spans
 * @returns the spans sorted chronologically
 */
function sortSpans(spans: EntrySpan[]): EntrySpan[] {
    return spans.sort((span1, span2) => {
        if (span1.start.isAfter(span1.end) || span2.start.isAfter(span2.end)) {
            return 0;
        } else if (span1.start.isBefore(span2.start) && span1.end.isSameOrBefore(span2.end)) {
            return -1;
        } else if (span1.start.isAfter(span2.start) && span1.end.isSameOrAfter(span2.end)) {
            return 1;
        } else if (span1.end.isBefore(span2.end)) {
            return -1;
        } else if (span1.end.isSame(span2.end)) {
            return 0;
        }
        return 1;
    });
}

/**
 * Port of `TimesheetModel.getTimesheetEntries`, spans only: the work entries of the confirmations
 * that were submitted plus the manual (gap-closing) entries, in chronological order. The work
 * entries of an open confirmation are left out — the end of work follows what was booked, and an
 * entry the technician is still working on has not been.
 *
 * The slice keeps `entries` sorted as a reducer invariant, but the ordering the end-of-work rule
 * needs is the legacy one, which tie-breaks on the entries' end times — so the spans are sorted
 * here rather than read in slice order.
 *
 * @param timesheet the timesheet slice to read
 * @returns the sorted entry spans
 */
function entrySpans(timesheet: TimesheetState): EntrySpan[] {
    const spans = timesheet.entries
        .filter((entry) => entry.kind !== 'work' || entry.isConfirmationSubmitted)
        .map((entry) => ({
            start: entry.start ? moment(entry.start) : moment.invalid(),
            // An unset end has not been typed yet, not "no end" — it falls back to the entry's own
            // start (a zero-length span) rather than staying invalid, or the entry would silently
            // drop out of the ordering and the end-of-work rules that read this span's end. Only
            // when the start is unset too does the span stay invalid.
            end: entry.end ? moment(entry.end) : entry.start ? moment(entry.start) : moment.invalid(),
        }));

    return sortSpans(spans);
}

/**
 * Port of `TimesheetModel.getLastEntryDateTime`: the end of the last entry, else the start of
 * work, else invalid for a completely empty timesheet.
 *
 * @param timesheet the timesheet slice to read
 * @returns the last recorded instant of the timesheet
 */
function lastEntryDateTime(timesheet: TimesheetState): moment.Moment {
    const spans = entrySpans(timesheet);
    if (spans.length > 0) {
        return spans[spans.length - 1].end.clone();
    }
    return timesheet.startOfWork ? moment(timesheet.startOfWork) : moment.invalid();
}

/**
 * Port of `TimesheetModel.getEndOfWork` for the current (never submitted) timesheet: the later of
 * the last entry's end time and the recorded end of work, falling back to "now" when the
 * timesheet holds neither.
 *
 * Deliberately a plain function rather than a selector: the "now" fallback has to be evaluated
 * per read, which a memoized selector would freeze until the slice next changes.
 *
 * @param timesheet the current timesheet slice
 * @returns the end-of-work instant
 */
export function currentEndOfWork(timesheet: TimesheetState): moment.Moment {
    const lastEntry = lastEntryDateTime(timesheet);
    const endOfWork = timesheet.endOfWork ? moment(timesheet.endOfWork) : moment.invalid();

    if (lastEntry.isValid() && (!endOfWork.isValid() || lastEntry.isAfter(endOfWork))) {
        return lastEntry.clone();
    } else if (!endOfWork.isValid()) {
        return moment().startOf('minute');
    }
    return endOfWork.clone();
}
